import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Check, X, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { questionariAPI } from '../../services/questionariApi';
import ElencoDomande from './ElencoDomande';
import { useUscitaProtetta } from '../../utils/uscitaProtetta';
import { sipuoChiudere } from '../../utils/salvaCampo';
import '../../styles/QuestionarioTablet.css';
import '../../styles/CompilaQuestionario.css';
import '../../styles/QuestionarioTabletConsole.css';
import '../../styles/CompilaQuestionarioConsole.css';

/**
 * CompilaQuestionario — il questionario compilato alla postazione.
 *
 * A CHI SERVE
 * A chi il tablet non lo usa. Una persona anziana davanti a 84
 * affermazioni da valutare da 1 a 6 o rinuncia o risponde a caso: in
 * entrambi i casi il dato non c'e'. Con il medico che legge e registra il
 * dato c'e'.
 *
 * SI SALVA DA SOLO, E NON E' UN VEZZO
 * Un questionario lungo con una persona anziana si interrompe: squilla il
 * telefono, entra qualcuno, il paziente si stanca. Se una interruzione
 * costasse tutto il lavoro fatto, la seconda volta nessuno ricomincia. Le
 * risposte si salvano al cambio di pagina e ogni mezzo minuto, e riaprendo
 * si riprende da dove si era rimasti.
 *
 * RESTA SCRITTO CHE L'HA COMPILATO UN OPERATORE
 * Il server registra `modalita = postazione` e chi ha operato. Un
 * questionario letto da un medico non e' la stessa misura di uno
 * autosomministrato, e chi confronta due tempi deve poterlo sapere.
 */

const PER_PAGINA = 8;
const OGNI_MS = 30000;

const CompilaQuestionario = ({ pazienteId, questionario, compilazioneId,
                               anagrafica = {}, onChiudi, onFatto }) => {
    const def = questionario.definizione || {};
    const item = useMemo(() => def.item || [], [def]);
    const scale = useMemo(
        () => new Map((def.scale_risposta || []).map((s) => [s.id, s])), [def]
    );

    const [risposte, setRisposte] = useState({});
    const [pagina, setPagina] = useState(0);
    const [salvataggio, setSalvataggio] = useState(null);   // testo sotto al piede
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState(null);
    const [confermaVuoti, setConfermaVuoti] = useState(false);
    const [caricamento, setCaricamento] = useState(true);
    /* UN SALVATAGGIO NON RIUSCITO SI DICHIARA, come un campo rosso. Con la
       rete giu', o un valore fuori intervallo (il server rifiuta la bozza
       intera con 400), le risposte restano solo qui: il cambio di schermata
       chiede, «Chiudi» chiede, e la pagina non si aggiorna da sola
       (utils/versioneInLinea.js). Trovato alla revisione del 2/10: il
       contenitore si salva da solo, quindi la rete dei campi trattenuti non
       lo guarda, e nessuno lo dichiarava. Torna falso al primo salvataggio
       riuscito: la bozza parte sempre intera. */
    const [nonSalvato, setNonSalvato] = useState(false);
    useUscitaProtetta(nonSalvato);

    // L'ultimo stato salvato, per non riscrivere quando non e' cambiato nulla
    const salvate = useRef('{}');
    const correnti = useRef({});
    const corpo = useRef(null);

    correnti.current = risposte;

    /* Una scheda clinica si compila sezione per sezione: «esame obiettivo
       addominale» e' un blocco che il medico fa tutto insieme, e spezzarlo
       a meta' per far tornare i conti di otto campi a pagina sarebbe
       comodo per il programma e scomodo per chi lavora. I questionari, che
       non hanno sezioni, restano a blocchi. */
    const blocchi = useMemo(() => {
        const sez = questionario.definizione?.sezioni;
        if (Array.isArray(sez) && sez.length) {
            return sez
                .map((x) => ({
                    titolo: `${x.n}. ${x.titolo}`,
                    nota: x.nota || null,
                    item: item.filter((it) => it.sezione === x.n),
                }))
                .filter((b) => b.item.length || b.nota);
        }
        const out = [];
        for (let i = 0; i < item.length; i += PER_PAGINA) {
            out.push({ titolo: null, nota: null, item: item.slice(i, i + PER_PAGINA) });
        }
        return out;
    }, [item, questionario]);

    const pagine = Math.max(1, blocchi.length);
    const blocco = blocchi[Math.min(pagina, pagine - 1)] || { item: [] };
    const diQuestaPagina = blocco.item;

    /* Quanto e' compilata ogni pagina. Su diciassette sezioni, sapere
       dov'e' rimasto il vuoto senza doverle riaprire una per una e' la
       differenza fra una scheda che si finisce e una che resta a meta'. */
    const statoPagine = useMemo(() => blocchi.map((b, i) => {
        const suoi = b.item.filter((it) => !it.da_anagrafica && !it.calcolato);
        const pieni = suoi.filter((it) => {
            const v = risposte[String(it.n)];
            return v !== undefined && v !== '';
        }).length;
        return {
            n: i + 1,
            etichetta: b.titolo ? b.titolo.replace(/^(\d+)\..*/, '$1') : String(i + 1),
            titolo: b.titolo || `Pagina ${i + 1}`,
            pieni,
            totale: suoi.length,
        };
    }), [blocchi, risposte]);
    const date = Object.keys(risposte).length;
    /* I campi presi dall'anagrafica e quelli calcolati non si contano fra
       i mancanti: non li compila nessuno, ci sono gia'. */
    const daCompilare = item.filter((it) => !it.da_anagrafica && !it.calcolato);
    const mancanti = daCompilare
        .filter((it) => risposte[String(it.n)] === undefined || risposte[String(it.n)] === '')
        .map((it) => it.n);

    /* Se la compilazione era gia' aperta - iniziata al tablet e non finita,
       o interrotta qui - si riprende con le risposte che ha. */
    useEffect(() => {
        let vivo = true;
        questionariAPI.dettaglio(compilazioneId)
            .then((r) => {
                if (!vivo) return;
                const gia = {};
                for (const x of r.data.data.righe || []) {
                    if (x.risposto) gia[String(x.n)] = x.valore;
                }
                setRisposte(gia);
                salvate.current = JSON.stringify(gia);
            })
            .catch(() => { /* nessuna risposta precedente: si parte da zero */ })
            .finally(() => { if (vivo) setCaricamento(false); });
        return () => { vivo = false; };
    }, [compilazioneId]);

    const salva = useCallback(async (silenzioso = true) => {
        const ora = JSON.stringify(correnti.current);
        if (ora === salvate.current) { setNonSalvato(false); return true; }
        try {
            const r = await questionariAPI.salva(compilazioneId, correnti.current, false);
            salvate.current = ora;
            setNonSalvato(false);
            setSalvataggio(silenzioso
                ? `salvato ${new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
                : r.data.message);
            return true;
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Salvataggio non riuscito');
            setNonSalvato(true);
            return false;
        }
    }, [compilazioneId]);

    // Ogni mezzo minuto, e comunque uscendo dalla schermata
    useEffect(() => {
        const t = setInterval(() => { salva(true); }, OGNI_MS);
        return () => { clearInterval(t); salva(true); };
    }, [salva]);

    const rispondi = (n, valore) => setRisposte((p) => {
        const nuove = { ...p, [String(n)]: valore };
        /* Il BMI non si scrive: si calcola da peso e altezza. Un campo che
           si puo' digitare a mano accanto ai due da cui dipende e' un campo
           che prima o poi non torna con loro. */
        const bmi = item.find((it) => it.calcolato === 'bmi');
        if (bmi && (n === bmi.da_peso || n === bmi.da_altezza)) {
            const kg = parseFloat(String(nuove[String(bmi.da_peso)] ?? '').replace(',', '.'));
            const cm = parseFloat(String(nuove[String(bmi.da_altezza)] ?? '').replace(',', '.'));
            nuove[String(bmi.n)] = (Number.isFinite(kg) && Number.isFinite(cm) && cm > 0)
                ? Math.round((kg / ((cm / 100) ** 2)) * 10) / 10
                : '';
        }
        return nuove;
    });

    const vaiA = async (p) => {
        await salva(true);
        setPagina(Math.max(0, Math.min(pagine - 1, p)));
        if (corpo.current) corpo.current.scrollTo({ top: 0, behavior: 'auto' });
    };

    const concludi = async (anchePerVuoti = false) => {
        if (mancanti.length && !anchePerVuoti) { setConfermaVuoti(true); return; }
        if (!date) { setErrore('Non c\'è niente da registrare'); return; }
        try {
            setInCorso(true);
            setErrore(null);
            const r = await questionariAPI.salva(compilazioneId, risposte, true);
            salvate.current = JSON.stringify(risposte);
            onFatto(r.data.message);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Registrazione non riuscita');
            if (JSON.stringify(risposte) !== salvate.current) setNonSalvato(true);
        } finally {
            setInCorso(false);
        }
    };

    if (caricamento) return <p className="qz-attesa">Caricamento…</p>;

    return (
        <div className="cq-wrap">
            <div className="cq-testa">
                <div>
                    <h3>{questionario.titolo_visualizzato || questionario.nome}</h3>
                    <span className="cq-meta">
                        {daCompilare.length - mancanti.length} compilati su {daCompilare.length}
                        {salvataggio && <> · <span className="cq-salvato">{salvataggio}</span></>}
                    </span>
                </div>
                <button className="qz-btn"
                        onClick={async () => { if ((await salva(true)) || sipuoChiudere(false, 1)) onChiudi(); }}>
                    <X size={14} /> Chiudi
                </button>
            </div>

            <div className="cq-barra">
                <span style={{ width: `${((daCompilare.length - mancanti.length) / Math.max(1, daCompilare.length)) * 100}%` }} />
            </div>

            {/* La consegna sta sotto gli occhi di chi legge al paziente:
                va letta a lui, non riassunta a memoria. */}
            {questionario.istruzioni && (
                <p className="cq-consegna">{questionario.istruzioni}</p>
            )}

            {errore && <p className="qz-errore"><AlertTriangle size={15} /> {errore}</p>}

            {/* LA BOZZA SI SALVA DA SOLA all'uscita da ogni risposta (regola di
                Alex del 24/09/2026), oltre che cambiando pagina, chiudendo e
                ogni mezzo minuto; se niente e' cambiato non parte niente.
                Niente «Salva»: «Registra» resta, perche' conclude. */}
            <div className="cq-corpo" data-salva-da-solo ref={corpo} onBlur={() => { salva(true); }}>
                {blocco.titolo && <h4 className="cq-sezione">{blocco.titolo}</h4>}
                {blocco.nota && <p className="cq-sezione-nota">{blocco.nota}</p>}
                <ElencoDomande item={diQuestaPagina} scale={scale} risposte={risposte}
                               onRispondi={rispondi} mancanti={[]} anagrafica={anagrafica} />
                {!diQuestaPagina.length && (
                    <p className="cq-sezione-nota">Nessun campo in questa sezione.</p>
                )}
            </div>

            {confermaVuoti && (
                <div className="cq-conferma">
                    <p>
                        <strong>
                            {mancanti.length === 1
                                ? 'Una domanda è rimasta senza risposta'
                                : `${mancanti.length} domande sono rimaste senza risposta`}
                        </strong>{' '}
                        ({mancanti.slice(0, 12).join(', ')}{mancanti.length > 12 ? '…' : ''}).
                        Si può registrare così: in cartella risulteranno non risposte.
                    </p>
                    <div className="qz-azioni">
                        <button className="qz-btn" onClick={() => setConfermaVuoti(false)}>
                            Le completo
                        </button>
                        <button className="qz-btn qz-btn--principale"
                                onClick={() => concludi(true)} disabled={inCorso}>
                            <Check size={14} /> Registra così
                        </button>
                    </div>
                </div>
            )}

            {/* Le pagine, tutte insieme. Su una scheda da diciassette
                sezioni il medico non compila in ordine: fa l'esame
                obiettivo mentre il paziente e' sul lettino e torna
                all'anamnesi dopo. Costringerlo a passare per «avanti»
                quattordici volte e' il modo di far compilare meno. */}
            {pagine > 1 && (
                <nav className="cq-mappa" aria-label="Sezioni della scheda">
                    {statoPagine.map((p, i) => {
                        const quota = p.totale ? p.pieni / p.totale : 0;
                        return (
                            <button key={i} type="button"
                                    className={`cq-tassello ${i === pagina ? 'qui' : ''}`
                                               + (p.totale && p.pieni === p.totale ? ' completa' : '')
                                               + (!p.totale ? ' senza-campi' : '')}
                                    title={`${p.titolo} — ${p.totale
                                        ? `${p.pieni} campi su ${p.totale}` : 'nessun campo'}`}
                                    onClick={() => vaiA(i)}
                                    disabled={inCorso}>
                                {p.etichetta}
                                <span className="cq-quota">
                                    <span style={{ width: `${quota * 100}%` }} />
                                </span>
                            </button>
                        );
                    })}
                </nav>
            )}

            <div className="cq-piede">
                <button className="qz-btn" onClick={() => vaiA(pagina - 1)}
                        disabled={pagina === 0 || inCorso}>
                    <ChevronLeft size={14} /> Indietro
                </button>
                <span className="cq-pagina">
                    {pagina + 1} di {pagine}
                    {blocco.titolo && <span className="cq-pagina-nome">{blocco.titolo}</span>}
                </span>
                <div className="qz-azioni">
                    {pagina < pagine - 1 ? (
                        <button className="qz-btn qz-btn--principale"
                                onClick={() => vaiA(pagina + 1)} disabled={inCorso}>
                            Avanti <ChevronRight size={14} />
                        </button>
                    ) : (
                        <button className="qz-btn qz-btn--principale"
                                onClick={() => concludi(false)} disabled={inCorso}>
                            <Check size={14} /> Registra
                        </button>
                    )}
                </div>
            </div>

            {pagina < pagine - 1 && (
                <p className="cq-nota">
                    Si può registrare da qualunque pagina, senza arrivare in fondo:{' '}
                    <button className="cq-inline" onClick={() => concludi(false)} disabled={inCorso}>
                        registra adesso
                    </button>
                </p>
            )}
        </div>
    );
};

export default CompilaQuestionario;
